import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Badge } from './Badge';

export interface KPICardBadge {
  label: string;
  variant: 'success' | 'warning' | 'danger' | 'info';
}

export interface KPICardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  value: string | number;
  subtitle?: string;
  badge?: KPICardBadge;
  /** Plusieurs badges cote a cote (ex : "X justifies" + "Y non justifies"). Ignore si `badge` est fourni. */
  badges?: KPICardBadge[];
  icon?: ReactNode;
}

const badgeVariantMap = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'primary',
} as const;

const KPICard = forwardRef<HTMLDivElement, KPICardProps>(
  ({ className, title, value, subtitle, badge, badges, icon, ...props }, ref) => {
    const resolvedBadges = badge ? [badge] : badges;
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl border border-neutral-200 bg-white p-4 sm:p-6 shadow-sm overflow-hidden',
          className
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <h3 className="text-xs sm:text-sm font-medium text-neutral-500">{title}</h3>
              {resolvedBadges?.map((b, i) => (
                <Badge key={i} variant={badgeVariantMap[b.variant]}>
                  {b.label}
                </Badge>
              ))}
            </div>
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-dark-500">
              {value}
            </div>
            {subtitle && (
              <p className="mt-1 text-xs sm:text-sm text-neutral-500">{subtitle}</p>
            )}
          </div>
          {icon && (
            <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
              {icon}
            </div>
          )}
        </div>
      </div>
    );
  }
);

KPICard.displayName = 'KPICard';

export { KPICard };
